import {useCompanyTariffGuard} from '../../../modules/company-tariff';
import {Autocomplete, Button, Divider, FormControl, Grid, TextField, Toolbar, Typography} from '@mui/material';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import {Stack} from '@mui/system';
import {get, omit} from 'lodash';
import {FC, SyntheticEvent, useCallback, useContext, useEffect, useMemo, useRef, useState} from 'react';
import {useNavigate} from 'react-router-dom';
import {useRecoilStateLoadable, useRecoilValue} from 'recoil';
import Fallback from '../../../ui/fallback/Fallback';

import LoadingButton from '@mui/lab/LoadingButton';
import {AdapterDateFns} from '@mui/x-date-pickers/AdapterDateFnsV3';
import {DateTimePicker} from '@mui/x-date-pickers/DateTimePicker';
import {LocalizationProvider} from '@mui/x-date-pickers/LocalizationProvider';
import {ru} from 'date-fns/locale';
import {Controller, FormProvider, SubmitHandler, useForm} from 'react-hook-form';
import {toast} from 'react-toastify';
import {v4 as uuidv4} from 'uuid';
import {ServiceRequestService} from '../../../api/services/serviceRequest.service';
import {getIsUserCustomer} from '../../../global/lib';
import {EventBusContext} from '../../../providers/EventBus';
import {getFullName} from '../../../shared/helpers/getFullName';
import {AppAny} from '../../../shared/types/common.types';
import {IContract} from '../../../shared/types/contract.types';
import {IDeviceTypesDict} from '../../../shared/types/deviceTypesDict.types';
import {authState} from '../../../store/auth/atoms';
import {companiesQuery} from '../../../store/company/selectors';
import {contractsQueryForNewSR} from '../../../store/contracts/selectors';
import {deviceTypesDictQuery} from '../../../store/deviceTypes/selectors';
import {EMPTY_SERVICE_REQUEST} from '../../../store/service-requests/atoms';
import ControlledInput from '../../../ui/controlled-input/ControlledInput';
import PhoneInput from '../../../ui/phone-input/PhoneInput';
import {AddressField} from './AddressField';
import {getServiceRequestDtoNew, renderNonOptionsText} from './utils';

const dividerStyle = {letterSpacing: 1.75, fontWeight: 600, color: '#696969', fontSize: '14px'};

const REQUIRED_EXT_NUMBER_DEVICE_TYPES = {
  ELECTRICITY_METER: 'Обязательно для заполнения',
} as const;

const formatPhoneNumber = (value?: string) => {
  if (!value) return '';

  const [phonePart, extPart] = value.split('#');
  const digits = (phonePart || '').replace(/\D/g, '');
  const normalized = digits.length === 11 && digits.startsWith('8') ? `7${digits.slice(1)}` : digits;

  if (normalized.length !== 11 || !normalized.startsWith('7')) {
    return value;
  }

  const formatted = `+7 (${normalized.slice(1, 4)}) ${normalized.slice(4, 7)}-${normalized.slice(7, 9)}-${normalized.slice(9, 11)}`;
  const ext = extPart?.replace(/\D/g, '');

  return ext ? `${formatted} #${ext}` : formatted;
};

const NewServiceRequest: FC = () => {
  const {checkTariff, tariffLoading} = useCompanyTariffGuard();
  const {user} = useRecoilValue(authState);
  const [{contents: contracts, state: contractsState}] = useRecoilStateLoadable(contractsQueryForNewSR);
  const [{contents: companies, state: companiesState}] = useRecoilStateLoadable(companiesQuery);
  const [{contents: deviceTypes, state: deviceTypesState}] = useRecoilStateLoadable(deviceTypesDictQuery);
  const formApi = useForm({
    defaultValues: EMPTY_SERVICE_REQUEST,
    mode: 'onChange',
  });

  const {
    control,
    handleSubmit,
    getValues,
    watch,
    setValue,
    formState: {errors},
    setError,
    clearErrors,
  } = formApi;

  const {bus} = useContext(EventBusContext);

  const [submitLoading, setSubmitLoading] = useState(false);
  const requestIdRef = useRef(uuidv4());
  const isCustomer = useMemo(() => getIsUserCustomer(user), [user]);

  const navigate = useNavigate();
  const {deviceType} = getValues();
  const extNumberRequired = get(REQUIRED_EXT_NUMBER_DEVICE_TYPES, deviceType?.code, false);

  const contractId = watch('contractId');
  const companyName = watch('companyName') || '';

  const loading = contractsState === 'loading' || companiesState === 'loading';

  const contractsOptions =
    contractsState === 'hasValue' && contracts.content?.length
      ? contracts.content.map((i: IContract) => ({
          value: i.id,
          label: `${i.counter}, ${i.companyName}, ${i.contractNumber || ''}`.trim(),
        }))
      : [];

  const deviceTypesOptions =
    deviceTypesState === 'hasValue' && deviceTypes.content?.length
      ? (deviceTypes.content.reduce((acc: AppAny, curr: IDeviceTypesDict) => {
          if (!curr.higherCode) {
            return [
              ...acc,
              {
                label: curr.name,
                value: curr,
              },
            ];
          }

          return acc;
        }, []) as AppAny[])
      : [];

  const contract: IContract = contractsOptions?.length
    ? contracts.content.find((i: IContract) => i.id === contractId)
    : null;

  const company = contract ? {label: contract.companyName, value: contract.companyId} : null;

  const applyCustomerDefaults = useCallback(() => {
    if (!isCustomer || !user) return;

    setValue('customer', getFullName(user), {shouldDirty: false, shouldValidate: true});
    setValue('customerPhone', formatPhoneNumber(user.phoneNumber), {shouldDirty: false, shouldValidate: true});
    setValue('customers', [{id: user.uid}], {shouldDirty: false, shouldValidate: true});
  }, [isCustomer, setValue, user]);

  const onSubmit: SubmitHandler<AppAny> = async (data) => {
    if (!data.contractId || !data.serviceType) {
      if (!data.contractId) {
        setError('contractId', {type: 'required', message: 'Обязательно для заполнения'});
      }
      if (!data.serviceType) {
        setError('serviceType', {type: 'required', message: 'Обязательно для заполнения'});
      }

      return;
    }

    setSubmitLoading(true);

    const extNumber = data.extNumber ? data.extNumber.trim() : null;
    const serviceType = get(data, 'serviceType.value', null);

    try {
      await ServiceRequestService.create(
        getServiceRequestDtoNew(omit({...data, extNumber, managerId: user?.id, serviceType}, ['companyName'])),
        requestIdRef.current,
      );
      toast.success('Заявка добавлена', {
        containerId: 'default',
        autoClose: 2000,
        hideProgressBar: true,
        pauseOnHover: false,
        draggable: false,
      });
      navigate('/serviceRequests');
    } catch (error) {
      console.error(error);
      bus?.emit('error', [error]);
    } finally {
      setSubmitLoading(false);
    }
  };

  const handleServiceTypeChange = (_: SyntheticEvent<Element, Event>, value: string) => {
    setValue('serviceType', value, {shouldDirty: true, shouldValidate: true});
  };

  useEffect(() => {
    if (contractsState === 'hasError') {
      bus?.emit('error', [contracts]);
    }

    if (companiesState === 'hasError') {
      bus?.emit('error', [companies]);
    }

    if (deviceTypesState === 'hasError') {
      bus?.emit('error', [deviceTypes]);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contractsState, companiesState, deviceTypesState]);

  useEffect(() => {
    if (!company) {
      setValue('companyName', EMPTY_SERVICE_REQUEST.companyName, {shouldDirty: true, shouldValidate: true});
      setValue('companyId', EMPTY_SERVICE_REQUEST.companyId, {shouldDirty: true, shouldValidate: true});
      setValue('serviceType', EMPTY_SERVICE_REQUEST.serviceType, {shouldDirty: true, shouldValidate: true});
      if (!isCustomer) {
        setValue('customers', [], {shouldDirty: true, shouldValidate: true});
      }

      return;
    }

    setValue('companyName', company.label, {shouldDirty: true, shouldValidate: true});
    setValue('companyId', company.value, {shouldDirty: true, shouldValidate: true});
    setValue(
      'serviceType',
      {
        label: get(contract, 'serviceType.name'),
        value: get(contract, 'serviceType'),
      },
      {shouldDirty: true, shouldValidate: true},
    );

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [company?.value, contract, isCustomer, setValue]);

  useEffect(() => {
    applyCustomerDefaults();
  }, [applyCustomerDefaults]);

  if (loading) {
    return <Fallback />;
  }

  return (
    <Box sx={{width: '100%'}}>
      <Paper sx={{width: '96%', margin: '0 auto', mt: 4, mb: 4}}>
        <Toolbar
          sx={{
            pl: {sm: 2},
            pr: {xs: 2, sm: 2},
          }}
        >
          <Typography sx={{flex: '1 1 100%'}} variant="h6" id="formTitle" component="div">
            Новая заявка
          </Typography>
        </Toolbar>
        <Box sx={{padding: 2}}>
          <FormProvider {...formApi}>
            <LocalizationProvider adapterLocale={ru} dateAdapter={AdapterDateFns}>
              <form
                onSubmit={(event) => {
                  event.preventDefault();
                  if (checkTariff()) void handleSubmit(onSubmit)(event);
                }}
              >
                <Stack direction="column" justifyContent="space-between" spacing={3}>
                  <Stack direction="row">
                    <Box sx={{borderBottom: 1, borderColor: 'divider', width: '100%', pb: 2}}>
                      <Grid container columns={12} xs={12} spacing={2}>
                        <Grid item xs={12}>
                          <Divider sx={dividerStyle} textAlign="left">
                            Основная информация
                          </Divider>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <Autocomplete
                            fullWidth
                            disablePortal
                            options={contractsOptions}
                            size="small"
                            onChange={(_, val) => {
                              setValue('contractId', (val as (typeof contractsOptions)[0])?.value, {
                                shouldDirty: true,
                              });
                              if (val) clearErrors('contractId');
                            }}
                            multiple={false}
                            openOnFocus
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                label="Контракт"
                                error={!!errors?.contractId}
                                helperText={errors?.contractId ? String(errors?.contractId?.message) : ''}
                              />
                            )}
                            noOptionsText={renderNonOptionsText('Контракты не найдены')}
                          />
                        </Grid>
                        <Grid key={companyName || 'companyName'} item xs={12} sm={6}>
                          <FormControl fullWidth>
                            <ControlledInput
                              name="companyName"
                              control={control}
                              disabled
                              fullWidth
                              size="small"
                              label="Компания"
                            />
                          </FormControl>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <Controller
                            name="serviceType"
                            control={control}
                            render={({field: {value}}) => (
                              <FormControl fullWidth>
                                <Autocomplete
                                  size="small"
                                  value={value}
                                  fullWidth
                                  disabled
                                  disablePortal
                                  openOnFocus
                                  filterOptions={(x) => x}
                                  onChange={handleServiceTypeChange}
                                  options={deviceTypesOptions}
                                  noOptionsText={renderNonOptionsText('Виды работ не найдены')}
                                  renderInput={(params) => (
                                    <TextField
                                      {...params}
                                      label="Вид работ"
                                      error={!!errors?.deviceType}
                                      helperText={errors?.deviceType ? String(errors?.deviceType?.message) : ''}
                                    />
                                  )}
                                />
                              </FormControl>
                            )}
                          />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <ControlledInput
                            control={control}
                            name="extNumber"
                            size="small"
                            label="Лицевой счет"
                            fullWidth
                            rules={{
                              required: extNumberRequired,
                            }}
                          />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <ControlledInput
                            control={control}
                            name="customer"
                            size="small"
                            label="Наименование абонента"
                            fullWidth
                            rules={{
                              required: 'Обязательно для заполнения',
                            }}
                          />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <FormControl fullWidth>
                            <PhoneInput
                              control={control}
                              name="customerPhone"
                              rules={{
                                pattern: {
                                  value: /^(\+7 \([1-9]\d{2}\) \d{3}-\d{2}-\d{2}( #\d+)?$)/,
                                  message: 'Некорректный номер или добавочный',
                                },
                              }}
                              size="small"
                              label="Номер телефона"
                            />
                          </FormControl>
                        </Grid>
                        <Grid item xs={12}>
                          <ControlledInput
                            control={control}
                            rows={4}
                            rules={{
                              maxLength: {
                                value: 1024,
                                message: 'Не более 1024 символов',
                              },
                            }}
                            multiline
                            name="description"
                            sx={{width: '100%', bgcolor: '#fff'}}
                            size="small"
                            label="Замечания / Комментарии"
                          />
                        </Grid>
                        <Grid item xs={12}>
                          <Divider sx={dividerStyle} textAlign="left">
                            Адрес
                          </Divider>
                        </Grid>
                        <Grid item xs={12}>
                          <AddressField />
                        </Grid>
                        <Grid item xs={12}>
                          <Divider sx={dividerStyle} textAlign="left">
                            Дата
                          </Divider>
                        </Grid>

                        <Grid item xs={12} sm={4}>
                          <Controller
                            control={control}
                            name="startDate"
                            render={({field: {value, onChange}}) => (
                              <DateTimePicker
                                label="Дата ввода заявки"
                                format="dd.MM.yyyy"
                                views={['year', 'month', 'day', 'hours', 'minutes']}
                                value={value}
                                onChange={onChange}
                                slotProps={{
                                  textField: {
                                    size: 'small',
                                    fullWidth: true,
                                  },
                                }}
                              />
                            )}
                          />
                        </Grid>
                        <Grid item xs={12} sm={4}>
                          <Controller
                            control={control}
                            name="plannedDate"
                            render={({field: {value, onChange}}) => (
                              <DateTimePicker
                                label="Дата окончания заявки"
                                format="dd.MM.yyyy"
                                views={['year', 'month', 'day', 'hours', 'minutes']}
                                value={value}
                                onChange={onChange}
                                slotProps={{
                                  textField: {
                                    size: 'small',
                                    fullWidth: true,
                                  },
                                }}
                              />
                            )}
                          />
                        </Grid>
                      </Grid>
                    </Box>
                  </Stack>
                  <Stack direction="row" justifyContent="space-between">
                    <Button
                      variant="outlined"
                      color="error"
                      type="button"
                      disabled={submitLoading}
                      onClick={() => navigate('/serviceRequests')}
                    >
                      Отменить
                    </Button>
                    <LoadingButton variant="outlined" type="submit" loading={submitLoading} disabled={tariffLoading}>
                      Сохранить
                    </LoadingButton>
                  </Stack>
                </Stack>
              </form>
            </LocalizationProvider>
          </FormProvider>
        </Box>
      </Paper>
    </Box>
  );
};

export default NewServiceRequest;
